import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { SquareDeviceTypeService } from '../services/square-device-type.service';
import * as SquareDeviceTypeActions from './square-device-type.actions';

@Injectable()
export class SquareDeviceTypeEffects {
    private _actions$ = inject(Actions);
    private _squareDeviceTypeService = inject(SquareDeviceTypeService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all square device types',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllSquareDeviceTypes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceTypeActions.loadSquareDeviceTypes),
            switchMap(() =>
                this._squareDeviceTypeService.getAll().pipe(
                    map((res) => {
                        return SquareDeviceTypeActions.loadSquareDeviceTypesSuccess(
                            {
                                squareDeviceTypes: res.data,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareDeviceTypeActions.loadSquareDeviceTypesFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Get One
    getSquareDeviceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceTypeActions.loadSquareDeviceType),
            switchMap((action) =>
                this._squareDeviceTypeService.getOne(action.id).pipe(
                    map((res) =>
                        SquareDeviceTypeActions.loadSquareDeviceTypeSuccess({
                            squareDeviceType: res.data,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceTypeActions.loadSquareDeviceTypeFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Create
    createSquareDeviceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceTypeActions.createSquareDeviceType),
            switchMap((action) =>
                this._squareDeviceTypeService.create(action.data).pipe(
                    map((res) =>
                        SquareDeviceTypeActions.createSquareDeviceTypeSuccess({
                            squareDeviceType: res.data,
                            success: 'Square device type created successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceTypeActions.createSquareDeviceTypeFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Update
    updateSquareDeviceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceTypeActions.updateSquareDeviceType),
            switchMap((action) =>
                this._squareDeviceTypeService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) =>
                            SquareDeviceTypeActions.updateSquareDeviceTypeSuccess(
                                {
                                    squareDeviceType: res.data,
                                    success:
                                        'Square device type updated successfully',
                                }
                            )
                        ),
                        catchError((error) =>
                            of(
                                SquareDeviceTypeActions.updateSquareDeviceTypeFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // Delete
    deleteSquareDeviceType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceTypeActions.deleteSquareDeviceType),
            switchMap((action) =>
                this._squareDeviceTypeService.delete(action.id).pipe(
                    map(() =>
                        SquareDeviceTypeActions.deleteSquareDeviceTypeSuccess({
                            id: action.id,
                            success: 'Square device type deleted successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceTypeActions.deleteSquareDeviceTypeFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareDeviceTypeActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareDeviceTypeActions.createSquareDeviceTypeSuccess,
                    SquareDeviceTypeActions.updateSquareDeviceTypeSuccess,
                    SquareDeviceTypeActions.deleteSquareDeviceTypeSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSquareDeviceTypeActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareDeviceTypeActions.loadSquareDeviceTypesFailure,
                    SquareDeviceTypeActions.loadSquareDeviceTypeFailure,
                    SquareDeviceTypeActions.createSquareDeviceTypeFailure,
                    SquareDeviceTypeActions.updateSquareDeviceTypeFailure,
                    SquareDeviceTypeActions.deleteSquareDeviceTypeFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
